import * as React from "react";
import { Label, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger, cn } from "@nexus/ui";

const NEXT_KEYS = new Set(["ArrowRight", "ArrowDown"]);
const PREVIOUS_KEYS = new Set(["ArrowLeft", "ArrowUp"]);
/** A quick pass must not open the description. Keyboard focus still opens it. */
const TOOLTIP_HOVER_DELAY_MS = 2000;
const SELECTION_INDICATOR_CLASS =
  "pointer-events-none absolute z-0 rounded-sm bg-background shadow-sm transition-transform duration-200 ease-out motion-reduce:transition-none";

interface SelectionIndicator {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface ChoiceCardOption<T extends string> {
  value: T;
  title: string;
  /** Decorative mark already used elsewhere in the product. Hidden from assistive tech. */
  icon?: React.ReactNode;
  /** Shown in a focus/hover tooltip. It does not add a second visual line. */
  description?: string;
}

export interface ChoiceCardGroupProps<T extends string> {
  id: string;
  label: React.ReactNode;
  options: readonly ChoiceCardOption<T>[];
  /** Empty string when nothing is picked yet — the first option then takes focus. */
  value: T | "";
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Keep the label for assistive tech when the options are the only visible copy. */
  hideLabel?: boolean;
  /** One column below the sm breakpoint so an extra card stays inside a phone-width dialog. */
  stackOnNarrow?: boolean;
}

/**
 * Single-line segmented choice. Both Generate Set pickers share one full-width
 * track with equal columns, so Meta lines up with Personas driven. Selection is
 * a filled segment that slides to the new choice; the focus outline appears
 * only while the control is focused.
 */
export function ChoiceCardGroup<T extends string>({
  id,
  label,
  options,
  value,
  onChange,
  disabled,
  hideLabel = false,
  stackOnNarrow = false,
}: ChoiceCardGroupProps<T>) {
  const trackRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef(new Map<string, HTMLButtonElement>());
  const [indicator, setIndicator] = React.useState<SelectionIndicator | null>(null);
  const labelId = `${id}-label`;
  const focusableValue = options.some((option) => option.value === value) ? value : options[0]?.value;

  const syncIndicator = React.useCallback(() => {
    const selected = optionRefs.current.get(value);
    if (!selected) {
      setIndicator(null);
      return;
    }
    const next = {
      left: selected.offsetLeft,
      top: selected.offsetTop,
      width: selected.offsetWidth,
      height: selected.offsetHeight,
    };
    setIndicator((current) =>
      current &&
      current.left === next.left &&
      current.top === next.top &&
      current.width === next.width &&
      current.height === next.height
        ? current
        : next,
    );
  }, [value]);

  React.useLayoutEffect(() => {
    syncIndicator();
  }, [syncIndicator, options]);

  React.useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const observer = new ResizeObserver(syncIndicator);
    observer.observe(track);
    return () => observer.disconnect();
  }, [syncIndicator]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>, optionValue: T) {
    if (event.key === " " || event.key === "Spacebar" || event.key === "Enter") {
      event.preventDefault();
      onChange(optionValue);
      optionRefs.current.get(optionValue)?.focus();
      return;
    }
    const step = NEXT_KEYS.has(event.key) ? 1 : PREVIOUS_KEYS.has(event.key) ? -1 : 0;
    if (step === 0 || options.length === 0) return;
    event.preventDefault();
    const index = Math.max(0, options.findIndex((option) => option.value === value));
    const next = options[(index + step + options.length) % options.length]!.value;
    onChange(next);
    optionRefs.current.get(next)?.focus();
  }

  const columnTemplate = `repeat(${Math.max(options.length, 1)}, minmax(max-content, 1fr))`;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label id={labelId} className={hideLabel ? "sr-only" : undefined}>
        {label}
      </Label>
      <TooltipProvider delayDuration={TOOLTIP_HOVER_DELAY_MS} skipDelayDuration={0}>
        <div className={cn(!stackOnNarrow && "overflow-x-auto")}>
        <div ref={trackRef} className={cn("relative w-full", stackOnNarrow ? "min-w-0" : "min-w-max")}>
        {indicator ? (
          <div
            data-choice-selection=""
            aria-hidden
            className={SELECTION_INDICATOR_CLASS}
            style={{
              width: indicator.width,
              height: indicator.height,
              transform: `translate(${indicator.left}px, ${indicator.top}px)`,
            }}
          />
        ) : null}
        <div
          id={id}
          role="radiogroup"
          aria-labelledby={labelId}
          className={cn(
            "grid w-full gap-1 rounded-md border border-border bg-muted/40 p-1.5",
            stackOnNarrow ? "grid-cols-1 min-w-0 sm:[grid-template-columns:var(--choice-columns)]" : "min-w-max",
          )}
          style={
            stackOnNarrow
              ? ({ "--choice-columns": columnTemplate } as React.CSSProperties)
              : { gridTemplateColumns: columnTemplate }
          }
        >
          {options.map((option) => {
            const checked = value === option.value;
            const optionId = `${id}-${option.value}`;
            const control = (
              <button
                ref={(node) => {
                  if (node) optionRefs.current.set(option.value, node);
                  else optionRefs.current.delete(option.value);
                }}
                id={optionId}
                type="button"
                role="radio"
                aria-checked={checked}
                aria-labelledby={`${optionId}-title`}
                aria-describedby={option.description ? `${optionId}-description` : undefined}
                tabIndex={option.value === focusableValue ? 0 : -1}
                disabled={disabled}
                onClick={() => onChange(option.value)}
                onKeyDown={(event) => handleKeyDown(event, option.value)}
                className={cn(
                  "relative z-[1] inline-flex h-11 min-h-11 min-w-11 items-center justify-center gap-2 whitespace-nowrap rounded-sm bg-transparent px-3 text-[13px]",
                  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                  "disabled:cursor-not-allowed disabled:opacity-60",
                  checked
                    ? "font-medium text-primary-subtle-foreground"
                    : "font-normal text-muted-foreground hover:text-foreground",
                )}
              >
                {option.icon}
                <span id={`${optionId}-title`}>{option.title}</span>
                {option.description ? (
                  <span id={`${optionId}-description`} className="sr-only">
                    {option.description}
                  </span>
                ) : null}
              </button>
            );

            if (!option.description) {
              return <React.Fragment key={option.value}>{control}</React.Fragment>;
            }

            return (
              <Tooltip key={option.value}>
                <TooltipTrigger asChild>{control}</TooltipTrigger>
                <TooltipContent className="max-w-56">{option.description}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
        </div>
        </div>
      </TooltipProvider>
    </div>
  );
}
